import React, { useCallback, useEffect, useRef, useState } from 'react';
import SpecialUsersTable from '../common/SpecialUsersTable';
import Badge from 'components/Badge/Badge';
import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic';
import AddUserDialog from 'views/Users/common/AddUserDialog';
import { LinkCell, UserNameCell } from 'components/Cells/Cells';
import Account from '@material-ui/icons/AccountCircleOutlined';
import { AccountsService } from 'services/accounts/accounts';
import { UsersService } from 'services/users/users';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { useDispatch, useSelector } from 'react-redux';
import { finalize } from 'rxjs/operators';
import invalidateCache from 'utils/api/invalidateCache';
import { setToast } from 'redux/modules/toast/actions';
import {
	accountPermissionsSelector,
	userTypeSelector,
	departmentPermissionsSelector,
} from 'redux/modules/session/selectors';
import { hasAccess } from 'utils/permissions/permissions';
import { translate } from 'components/Translation';
import { SingleUser } from 'utils/api/models/users/singleUser';
import { SingleDepartment } from 'utils/api/models/departments/singleDepartment';
import { SingleCorpAccount } from 'utils/api/models/accounts/singleCorpAccount';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { exportToXLS } from 'utils/export/export';
import moment from 'moment';

interface IOrganizationAdminsProps {
	match: any;
	departments: SingleDepartment[];
}

const OrganizationAdmins = (props: IOrganizationAdminsProps) => {
	const [admins, setAdmins] = useState<Array<any>>([]);
	const [selectedIndexes, setSelectedIndexes] = useState<number[]>([]);
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [loadingAdmins, setLoadingAdmins] = useState(false);
	const [loadingAccounts, setLoadingAccounts] = useState(false);
	const [accounts, setAccounts] = useState<SingleCorpAccount[]>([]);
	const { accountId } = props.match.params;
	const { departments } = props;
	const dispatch = useDispatch();
	const accountPermissions = useSelector(accountPermissionsSelector);
	const departmentPermissions = useSelector(departmentPermissionsSelector);
	const loggedUserType = useSelector(userTypeSelector);
	const isMounted = useRef(true);

	const userAddedSuccessfully = () => {
		fetchAdmins();
		fetchAccounts();
	};

	const fetchAdmins = useCallback(() => {
		setLoadingAdmins(true);
		AccountsService.getCorpAccountAdmins(accountId)
			.pipe(finalize(() => isMounted.current && setLoadingAdmins(false)))
			.subscribe(({ admins }) => {
				if (isMounted.current) {
					setAdmins(
						admins.map((admin: SingleUser) => {
							const department = departments.find(
								(department) => department.deptId === admin.departmentId
							);
							return {
								...admin,
								department,
								accessible: hasAccess(admin, loggedUserType, accountPermissions, departmentPermissions),
							};
						})
					);
				}
			});
	}, [accountId, departments, loggedUserType, accountPermissions, departmentPermissions]);

	const fetchAccounts = useCallback(() => {
		setLoadingAccounts(true);
		AccountsService.getCorpAccountsWithAdminsForCurrentUser()
			.pipe(finalize(() => isMounted.current && setLoadingAccounts(false)))
			.subscribe(({ accounts }) => {
				if (isMounted.current) {
					setAccounts(accounts);
				}
			});
	}, []);

	useEffect(() => {
		fetchAdmins();
		window.scrollTo({
			top: 0
		});
	}, [fetchAdmins]);

	useEffect(() => {
		fetchAccounts();
	}, [fetchAccounts]);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	const findAccount = (id: string) => {
		return accounts.find((account) => account.accountId === id);
	};

	useEffect(() => {
		if (!loadingAccounts && !loadingAdmins) {
			dispatch(hideLoadingIndicator());
		} else {
			dispatch(showLoadingIndicator());
		}
	});

	const deleteItems = () => {
		const userIds: Array<string> = selectedIndexes.map((index: number) => admins[index].userId);
		dispatch(showLoadingIndicator());
		UsersService.deleteAccountPermissionsForCorpUsers({
			accountId,
			userIds,
		})
			.pipe(finalize(() => dispatch(hideLoadingIndicator())))
			.subscribe(({ response }) => {
				if (response.deleteAccountPermissionsForCorpUsersResponse.result) {
					setSelectedIndexes([]);
					setAdmins((_admins) => _admins.filter((admin: SingleUser) => userIds.indexOf(admin.userId) < 0));
					invalidateCache(
						[
							'getCorpAccountDetails',
							'getCorpAccountAdmins',
							'getDepartmentsForCurrentUser',
							'getDepartmentDetails',
							'getCorpUserDetails',
							'getCorpAccountsWithAdminsForCurrentUser',
						],
						true
					);
					dispatch(
						setToast({
							error: false,
							label: translate('organizations.admins.deleted'),
							actionType: translate('deleted'),
						})
					);
					fetchAccounts();
				}
			});
	};

	const exportToXls = useCallback(() => {
		const preparedData = (selectedIndexes.length === 0
			? admins
			: selectedIndexes.map((index) => admins[index])
		).map((admin) => ({
			'User name': admin.userName,
			'Department name': admin.department ? admin.department.name : '',
		}));

		exportToXLS(preparedData, `admins ${moment().format('LLL')}`);
	}, [selectedIndexes, admins]);

	return (
		<div>
			<BreadcrumbsItem to={props.match.url}>{translate('organizations.organization.admins')}</BreadcrumbsItem>
			<SpecialUsersTable
				selectedIndexes={selectedIndexes}
				button={{
					text: translate('add.admin'),
					onClick: () => setIsDialogOpen(true),
					disabled: admins.length >= 2,
					action: 'organizations:add-admin',
				}}
				deleteItems={deleteItems}
				exportToXLS={exportToXls}
				deleteItemsAction="organization-admins:delete-admins"
				setSelectedIndexes={setSelectedIndexes}
				header={translate('organizations.organization.admins')}
				data={admins}
				columns={[
					{
						Header: translate('table.user.name'),
						Cell: ({ original }: { original: any }) => (
							<>
								<ConditionalRender
									show={original.accessible}
									onTrue={() => (
										<LinkCell
											path={`/dashboard/users/${original.userId}/user-details`}
											text={original.userName}
											icon={<Account />}
										/>
									)}
									onFalse={() => <UserNameCell text={original.userName} type={'ADMIN'} />}
								/>
								<ConditionalRender
									show={!!original.department}
									onTrue={() => (
										<Badge hexColor={original.department.uiColor} filled>
											{original.department.name}
										</Badge>
									)}
								/>
							</>
						),
						accessor: 'userName',
					},
				]}
				text={translate('organization.admins.only.two.admins')}
			/>
			<AddUserDialog
				isDialogOpen={isDialogOpen}
				setIsDialogOpen={(value) => setIsDialogOpen(value)}
				dialogHeader={translate('add.admin')}
				userType="ADMIN"
				accounts={accounts}
				departments={departments as any}
				account={findAccount(accountId)}
				userAddedSuccessfully={userAddedSuccessfully}
				toastSuccessMessage={translate('admin.user.added')}
			/>
		</div>
	);
};

export default OrganizationAdmins;
